import { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { supabase } from '@/integrations/supabase/client';
import { LogOut, Mail, Clock, CheckCircle, Settings, User, Calendar } from 'lucide-react';
import PasswordChangeDialog from '@/components/PasswordChangeDialog';

interface ContactMessage {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  subject: string;
  message: string;
  service_type: string;
  created_at: string;
  read_at: string | null;
}

const AdminDashboard = () => {
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  
  const { user, token, logout, isLoading: authLoading } = useAdminAuth();
  const { toast } = useToast();

  const fetchMessages = async () => {
    if (!token) return;

    try {
      const { data, error } = await supabase.functions.invoke('contact-messages?action=list', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (error) throw error;

      setMessages(data.messages || []);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      toast({
        title: 'Error',
        description: 'Failed to load messages',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const markAsRead = async (messageId: string) => {
    if (!token) return;

    try {
      await supabase.functions.invoke('contact-messages?action=mark-read', {
        method: 'POST',
        body: { messageId },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // Update local state
      setMessages(prev => 
        prev.map(msg => 
          msg.id === messageId 
            ? { ...msg, read_at: new Date().toISOString() }
            : msg
        )
      );

      toast({
        title: 'Success',
        description: 'Message marked as read'
      });
    } catch (error) {
      console.error('Failed to mark message as read:', error);
      toast({
        title: 'Error',
        description: 'Failed to update message',
        variant: 'destructive'
      });
    }
  };

  useEffect(() => {
    if (token) {
      fetchMessages();
    }
  }, [token]);

  const handleLogout = () => {
    logout();
    toast({
      title: 'Logged out',
      description: 'You have been logged out successfully'
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle redirects and loading states after all hooks
  if (!authLoading && !user) {
    return <Navigate to="/admin/login" replace />;
  }

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  const unreadCount = messages.filter(msg => !msg.read_at).length;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/10 bg-background/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-xl font-bold text-glow-cyber">
                Belada.at
              </Link>
              <Badge variant="secondary" className="bg-gradient-cyber text-primary-foreground">
                Admin Panel
              </Badge>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <User className="w-4 h-4" />
                {user?.username}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsPasswordDialogOpen(true)}
                className="glass border-cyber-blue text-cyber-blue hover:bg-cyber-blue/10"
              >
                <Settings className="w-4 h-4 mr-2" />
                Change Password
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="glass border-cyber-red text-cyber-red hover:bg-cyber-red/10"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2 text-glow-cyber">Contact Messages</h1>
          <p className="text-muted-foreground">
            Manage and review messages from your website visitors
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="glass p-6 border-glow">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-gradient-cyber">
                <Mail className="w-6 h-6 text-primary-foreground" />
              </div>
              <div>
                <p className="text-2xl font-bold">{messages.length}</p>
                <p className="text-sm text-muted-foreground">Total Messages</p>
              </div>
            </div>
          </Card>
          
          <Card className="glass p-6 border-glow">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-gradient-web">
                <Clock className="w-6 h-6 text-primary-foreground" />
              </div>
              <div>
                <p className="text-2xl font-bold">{unreadCount}</p>
                <p className="text-sm text-muted-foreground">Unread Messages</p>
              </div>
            </div>
          </Card>
          
          <Card className="glass p-6 border-glow">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-gradient-software">
                <CheckCircle className="w-6 h-6 text-primary-foreground" />
              </div>
              <div>
                <p className="text-2xl font-bold">{messages.length - unreadCount}</p>
                <p className="text-sm text-muted-foreground">Read Messages</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Messages List */}
        <div className="space-y-4">
          {messages.length === 0 ? (
            <Card className="glass p-8 text-center border-glow">
              <Mail className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No messages yet</h3>
              <p className="text-muted-foreground">
                Contact messages will appear here when visitors submit the contact form.
              </p>
            </Card>
          ) : (
            messages.map((message) => (
              <Card key={message.id} className={`glass p-6 border-glow ${!message.read_at ? 'ring-2 ring-cyber-blue/20' : ''}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">
                        {message.first_name} {message.last_name}
                      </h3>
                      {!message.read_at && (
                        <Badge variant="default" className="bg-cyber-blue text-primary-foreground">
                          New
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    {formatDate(message.created_at)}
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Email:</span>
                      <p className="font-medium">{message.email}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Subject:</span>
                      <p className="font-medium">{message.subject}</p>
                    </div>
                    {message.service_type && (
                      <div>
                        <span className="text-muted-foreground">Service Type:</span>
                        <p className="font-medium">{message.service_type}</p>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <span className="text-muted-foreground text-sm">Message:</span>
                    <p className="mt-1 p-3 bg-muted/30 rounded-lg whitespace-pre-wrap">
                      {message.message}
                    </p>
                  </div>
                  
                  {!message.read_at && (
                    <div className="flex justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => markAsRead(message.id)}
                        className="glass border-cyber-green text-cyber-green hover:bg-cyber-green/10"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Mark as Read
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            ))
          )}
        </div>
      </main>

      <PasswordChangeDialog 
        open={isPasswordDialogOpen} 
        onOpenChange={setIsPasswordDialogOpen} 
      />
    </div>
  );
};

export default AdminDashboard;